import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MDButton from "components/MDButton";
import { useLocation, useNavigate } from "react-router-dom";
import { STILO } from "utilities/constants";
import { getData } from "utilities/common";

function hasAnyValue(obj) {
  for (const key in obj) {
    if (
      obj.hasOwnProperty(key) &&
      ((typeof obj[key] === "object" && obj[key].length > 0) ||
        (typeof obj[key] !== "object" && obj[key] !== "" && obj[key] !== "all"))
    ) {
      return true;
    }
  }
  return false;
}

export default function CategoryFilter(props = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  const categoryType = params.get("categoryTypes"); // e.g. "Cannabis"
  // State for filter form data
  const [formData, setFormData] = useState({
    categories: [],
    categoryTypes: '',
    status: "all",
  });
  const adminsMode = getData("adminsMode");
  const isStilo = JSON.parse(adminsMode) === STILO;
  const { applyCategoryFilter, getQueryValues = () => { }, setIsLoading = () => { } } = props;

  const { categoriesData = [] } = useSelector((state) => state.common);

  const categoryTypes = [
    { value: "Cannabis", label: "Cannabis" },
    { value: "Non-Cannabis", label: "Non Cannabis" },
  ];

  // Check if all categories are selected
  const isAllCategoriesSelected = useMemo(() => {
    return (
      categoriesData?.categories?.length > 0 &&
      formData?.categories.length === categoriesData?.categories?.length
    );
  }, [formData?.categories, categoriesData?.categories]);

  const applyFiltersHandler = () => {
    const { categories, categoryTypes, status } = formData || {};

    // 1. Map category names to IDs
    const categoryIds =
      categories
        ?.map(
          (categoryName) =>
            categoriesData?.categories?.find((category) => category?.categoryName === categoryName)
              ?.categoryId
        )
        .filter(Boolean) || [];

    // 2. Prepare queryPayload
    const queryPayload = {};
    queryPayload.status = status === 'all' ? '' : status;
    queryPayload.categoryTypes = categoryTypes ? categoryTypes : "";

    // 3. Prepare payload
    const payload = {};
    if (categoryIds.length > 0) payload.categoryIds = categoryIds;
    payload.categoryTypes = categoryTypes ? categoryTypes : "";

    const filterValue = { ...payload, ...queryPayload };

    // 4. Preserve existing query params
    const existingParams = new URLSearchParams(location.search);

    // 5. Merge updated filters
    Object.entries(filterValue).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        // For arrays, still use JSON format
        existingParams.set(key, JSON.stringify(value));
      } else if (typeof value === "string" && value.trim() !== "") {
        existingParams.set(key, value);
      }
    });


    if (categoryIds.length === 0) {
      existingParams.delete("categoryIds");
    }
    if (status === 'all') {
      existingParams.delete("status");
    }
    if (categoryTypes === '') {
      existingParams.delete("categoryTypes");
    }
    navigate(`${location.pathname}?${existingParams.toString()}`);

    applyCategoryFilter({ queryPayload, payload });
  };

  // Load filters from URL on component mount
  useEffect(() => {
    if (location.search && categoriesData?.categories?.length) {
      const searchParams = new URLSearchParams(location.search);

      // Get status from URL
      const status = searchParams?.get("status") || "all";

      // Get category types from URL as string
      const categoryTypes = searchParams?.get("categoryTypes") || '';

      // Get categories from URL
      let categories = [];
      if (searchParams?.get("categoryIds")) {
        try {
          const categoryIds = JSON.parse(searchParams?.get("categoryIds")) || [];
          categories =
            categoryIds
              ?.map(
                (categoryId) =>
                  categoriesData?.categories?.find(
                    (category) => category?.categoryId === categoryId
                  )?.categoryName
              )
              .filter(Boolean) || [];
        } catch (e) {
          categories = [];
        }
      }

      // Set form data
      setFormData({
        categories,
        categoryTypes,
        status,
      });

      // Call parent callback with initial values
      const queryPayload = {
        status: status === "all" ? "" : status,
        categoryTypes,
      };

      const payload = {
        categoryId: searchParams?.get("categoryIds")
          ? JSON.parse(searchParams?.get("categoryIds"))
          : [],
        categoryType: categoryType ? categoryType : "",
      };

      getQueryValues({ queryPayload, payload });
    }

    // If no search parameters, set loading to false
    if (!location.search) {
      setIsLoading(false);
    }
  }, [location.search, categoriesData?.categories]);

  // Clear all filters
  const clearAllHandler = () => {
    setFormData({
      categories: [],
      categoryTypes: '',
      status: "all",
    });

    const params = new URLSearchParams(location.search);

    // Remove specific params
    params.delete("status");
    params.delete("categoryIds");
    params.delete("categoryTypes");

    // Update the URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`);

    // Apply empty filter
    applyCategoryFilter({});
  };

  // Check if apply button should be shown
  const showApplyButton = useMemo(() => {
    return hasAnyValue(formData);
  }, [formData]);

  // Select all styles
  const selectAllStyles = {
    m: 0,
    mb: 0.5,
    padding: "4.5px 8px",
    borderRadius: "0.375rem",
    transition: "background-color 300ms ease, color 300ms ease",
    ":hover": {
      backgroundColor: "#f0f2f5",
    },
  };

  return (
    <Box className="cus-filter-sec" mb={2}>
      <Grid
        className="filter-box"
        container
        alignItems="center"
        justifyContent="flex-end"
        spacing={1.5}
      >
        {/* Categories Filter */}
        <Grid item xs={12} sm={4} md={3} lg={2.5}>
          <Box className="filter-categories">
            <FormControl fullWidth>
              <InputLabel id="categories-select-label">Categories</InputLabel>
              <Select
                size="small"
                labelId="categories-select-label"
                id="categories-select"
                multiple
                value={formData?.categories || []}
                fullWidth
                label="Categories"
                className="form-select"
                onChange={(event) => {
                  setFormData({ ...formData, categories: event.target.value });
                }}
                renderValue={(selected) =>
                  isAllCategoriesSelected ? "All Categories" : selected.join(", ")
                }
              >
                {/* Select All Option */}
                <FormControlLabel
                  sx={{
                    ...selectAllStyles,
                    backgroundColor: isAllCategoriesSelected ? "#f0f2f5" : "",
                  }}
                  control={
                    <Checkbox
                      sx={{ p: 0.4, mr: 0.5 }}
                      checked={isAllCategoriesSelected}
                      indeterminate={
                        formData?.categories.length > 0 &&
                        formData?.categories.length < categoriesData?.categories?.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            categories: categoriesData?.categories?.map(
                              (item) => item?.categoryName
                            ),
                          });
                        } else {
                          setFormData({ ...formData, categories: [] });
                        }
                      }}
                    />
                  }
                  label="Select All"
                />

                {/* Individual Category Options */}
                {categoriesData?.categories?.map((category) => (
                  <MenuItem sx={{ px: 1 }} key={category.categoryId} value={category?.categoryName}>
                    <Checkbox
                      sx={{ p: 0.4, mr: 1 }}
                      checked={formData?.categories.indexOf(category?.categoryName) > -1}
                    />
                    {category?.categoryName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        {/* Category Types Filter */}
        {isStilo && (
          <Grid item xs={12} sm={4} md={3} lg={2.5}>
            <Box className="filter-category-types">
              <FormControl fullWidth>
                <InputLabel id="category-types-select-label">Category Type</InputLabel>
                <Select
                  size="small"
                  labelId="category-types-select-label"
                  id="category-types-select"
                  value={formData?.categoryTypes || ''}
                  fullWidth
                  onChange={(event) => {
                    setFormData({ ...formData, categoryTypes: event.target.value });
                  }}
                  input={<OutlinedInput label="Category Type" />}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {categoryTypes?.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        )}

        {/* Status Filter */}
        <Grid item xs={12} sm={4} md={3} lg={2}>
          <Box className="filter-status">
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                size="small"
                value={formData?.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                fullWidth
                className="form-select"
                name="status"
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12} sm={12} md={3} lg={2}>
          <Box className="filter-btns">
            <Stack direction="row" alignItems="center" gap={1} className="w-100">
              <Button
                className="filter-clear-all"
                onClick={clearAllHandler}
                size="small"
                variant="text"
                color="secondary"
                style={{ width: "100%", fontWeight: "normal" }}
              >
                Clear All
              </Button>
              <MDButton
                // disabled={!showApplyButton}
                className="filter-apply"
                onClick={applyFiltersHandler}
                size="small"
                variant="contained"
                color="primary"
                style={{ width: "100%" }}
              >
                Apply
              </MDButton>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}